import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import {
  termsService,
  type Term,
  type TermWithTaxonomy,
  type CreateTermDto,
  type UpdateTermDto,
  type TermFilters,
} from '../services/taxonomies.service';
import {
  createOptimisticItem,
  onMutateCreate,
  onMutateDelete,
  onError,
  onSuccessCreate,
  onSettled,
} from '../lib/optimisticHelpers';

// Re-export types for convenience
export type {
  Term,
  TermWithTaxonomy,
  CreateTermDto,
  UpdateTermDto,
  TermFilters,
};

/**
 * Result interface for useTerms hook
 */
interface UseTermsResult {
  terms: TermWithTaxonomy[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createTerm: (data: CreateTermDto) => Promise<void>;
  updateTerm: (id: string, data: UpdateTermDto) => Promise<void>;
  deleteTerm: (id: string) => Promise<void>;
  hardDeleteTerm: (id: string) => Promise<void>;
}

/**
 * Custom hook for managing terms with React Query
 *
 * Benefits:
 * - Automatic request deduplication
 * - Intelligent caching (5 min stale time)
 * - Automatic background refetching
 * - Optimistic updates for better UX
 * - Error handling and retry logic
 *
 * @param filters - Optional filters for terms
 */
export const useTerms = (filters?: TermFilters): UseTermsResult => {
  const queryClient = useQueryClient();

  // Main query for fetching terms
  const {
    data: terms = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.terms.list(filters),
    queryFn: () => termsService.getAll(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  const error = queryError ? (queryError as Error).message : null;

  // Create term mutation
  const createTermMutation = useMutation({
    mutationFn: termsService.create,
    onMutate: async (newTerm) => {
      const optimisticTerm = createOptimisticItem<TermWithTaxonomy>({
        ...newTerm,
        isActive: newTerm.isActive ?? true,
        usageCount: 0,
        taxonomy: {
          id: newTerm.taxonomyId,
          name: 'Loading...',
          slug: 'loading',
        },
      });
      return onMutateCreate(
        queryClient,
        queryKeys.terms.list(filters),
        optimisticTerm
      );
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.terms.list(filters), context);
    },
    onSuccess: (data) => {
      onSuccessCreate(queryClient, queryKeys.terms.list(filters), data);
      // Also invalidate taxonomies since term counts changed
      queryClient.invalidateQueries({ queryKey: queryKeys.taxonomies.all });
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.terms.all);
    },
  });

  // Update term mutation
  const updateTermMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTermDto }) =>
      termsService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.terms.all });

      const previousData = queryClient.getQueryData<TermWithTaxonomy[]>(
        queryKeys.terms.list(filters)
      );

      queryClient.setQueryData<TermWithTaxonomy[]>(
        queryKeys.terms.list(filters),
        (old) =>
          old?.map((term) => (term.id === id ? { ...term, ...data } : term)) ??
          []
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.terms.list(filters),
          context.previousData
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.taxonomies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
    },
  });

  // Delete term mutation (soft delete)
  const deleteTermMutation = useMutation({
    mutationFn: termsService.delete,
    onMutate: async (id) => {
      return onMutateDelete(queryClient, queryKeys.terms.list(filters), id);
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.terms.list(filters), context);
    },
    onSuccess: () => {
      onSettled(queryClient, queryKeys.terms.all);
      queryClient.invalidateQueries({ queryKey: queryKeys.taxonomies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
    },
  });

  // Hard delete term mutation
  const hardDeleteTermMutation = useMutation({
    mutationFn: termsService.hardDelete,
    onMutate: async (id) => {
      return onMutateDelete(queryClient, queryKeys.terms.list(filters), id);
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.terms.list(filters), context);
    },
    onSuccess: () => {
      onSettled(queryClient, queryKeys.terms.all);
      queryClient.invalidateQueries({ queryKey: queryKeys.taxonomies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
    },
  });

  return {
    terms,
    loading,
    error,
    refetch: async () => {
      await refetch();
    },
    createTerm: async (data: CreateTermDto) => {
      await createTermMutation.mutateAsync(data);
    },
    updateTerm: async (id: string, data: UpdateTermDto) => {
      await updateTermMutation.mutateAsync({ id, data });
    },
    deleteTerm: async (id: string) => {
      await deleteTermMutation.mutateAsync(id);
    },
    hardDeleteTerm: async (id: string) => {
      await hardDeleteTermMutation.mutateAsync(id);
    },
  };
};

/**
 * Hook to fetch a single term by ID or slug
 */
export const useTerm = (idOrSlug: string, taxonomyId?: string) => {
  return useQuery({
    queryKey: queryKeys.terms.detail(idOrSlug),
    queryFn: () => termsService.getById(idOrSlug, taxonomyId),
    enabled: !!idOrSlug,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch terms grouped by taxonomy
 * Useful for rendering categorized term lists
 */
export const useTermsGrouped = () => {
  return useQuery({
    queryKey: queryKeys.terms.grouped(),
    queryFn: () => termsService.getGrouped(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch terms for a specific taxonomy
 * Supports both taxonomyId and taxonomySlug for flexibility
 * Use enabled=false for lazy loading (e.g., on accordion expand)
 */
export const useTermsByTaxonomy = (
  identifier: string,
  options?: {
    useId?: boolean;
    enabled?: boolean;
  }
) => {
  const { useId = false, enabled = true } = options || {};

  return useQuery({
    queryKey: queryKeys.terms.byTaxonomy(identifier),
    queryFn: () =>
      termsService.getAll(
        useId ? { taxonomyId: identifier } : { taxonomySlug: identifier }
      ),
    enabled: enabled && !!identifier,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // Don't refetch on window focus for better UX
  });
};
